import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Descriptions, Table, Collapse, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { EditOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';
import withStyles from 'react-jss';

import {
  getRpoItemsForStoreInward,
  clearRpoItemsForStoreInward,
  getRpoInfo,
  clearRpoInfo,
  getStoreInwardsOfRpo,
  clearStoreInwardsofRpo,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import styles from '../styles';

const { Panel } = Collapse;

const ViewRPOInwards = props => {
  const { match, classes } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const renukaPOInfo = useSelector(state => state.renukaPOInfo);
  const rpoItemsForStoreInward = useSelector(
    state => state.rpoItemsForStoreInward
  );
  const storeInwardsOfRpo = useSelector(state => state.storeInwardsOfRpo);

  useEffect(() => {
    dispatch(getRpoInfo(match.params.rpoId));
    dispatch(getRpoItemsForStoreInward(match.params.rpoId));
    dispatch(getStoreInwardsOfRpo(match.params.rpoId));
    return () => {
      dispatch(clearRpoInfo());
      dispatch(clearRpoItemsForStoreInward());
      dispatch(clearStoreInwardsofRpo());
    };
  }, []);

  const fabricRowKey = useMemo(
    () => record => `P${record.productId}F${record.fabricId}C${record.colorId}`,
    []
  );

  const accessoryRowKey = useMemo(
    () => record => `P${record.productId}A${record.accessoryId}`,
    []
  );

  const itemRowKey = useMemo(
    () => record =>
      `N${record.name}S${record.shade}S${record.size}U${record.unitId}`,
    []
  );

  const styleRowKey = useMemo(
    () => record =>
      `P${record.productId}S${record.styleId}D${record.description}S${record.size}B${record.baseColor}`,
    []
  );

  const scroll = useMemo(() => ({ x: 2200 }), []);

  const fabricsColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const accessoryColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const itemsColumns = useMemo(
    () => [
      {
        title: 'Item',
        dataIndex: 'name',
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const stylesColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Rec.',
        dataIndex: 'totalReceived',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const fabricsCollapseColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const accessoryCollapseColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const itemsCollapseColumns = useMemo(
    () => [
      {
        title: 'Item',
        dataIndex: 'name',
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  const stylesCollapseColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
      },
      {
        title: 'Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
      },
      {
        title: 'Invoice',
        dataIndex: 'invoice',
      },
      {
        title: 'Challan',
        dataIndex: 'challan',
      },
      {
        title: 'HSN Code',
        dataIndex: 'hsnNo',
      },
    ],
    []
  );

  return (
    <>
      <Breadcrumbs
        moduleName="Store Inward"
        moduleLink="/storeinward/list"
        subModuleName="RPO View"
      />
      <div className={classes.padding20}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="RPO #">
            {renukaPOInfo.rpoNo}
          </Descriptions.Item>
          <Descriptions.Item label="Vendor">
            {renukaPOInfo.vendor}
          </Descriptions.Item>
          <Descriptions.Item label="BPO #">
            {renukaPOInfo.bpoNo}
          </Descriptions.Item>
          <Descriptions.Item label="Customer">
            {renukaPOInfo.customer}
          </Descriptions.Item>
          <Descriptions.Item label="Process">
            {renukaPOInfo.process}
          </Descriptions.Item>
        </Descriptions>
        <br />
        {rpoItemsForStoreInward.fabrics.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={fabricsColumns}
              dataSource={rpoItemsForStoreInward.fabrics}
              rowKey={fabricRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Fabrics Table'}
            />
            <br />
          </>
        ) : null}
        {rpoItemsForStoreInward.accessories.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={accessoryColumns}
              dataSource={rpoItemsForStoreInward.accessories}
              rowKey={accessoryRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Accessories Table'}
            />
            <br />
          </>
        ) : null}
        {rpoItemsForStoreInward.items.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={itemsColumns}
              dataSource={rpoItemsForStoreInward.items}
              rowKey={itemRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Items Table'}
            />
            <br />
          </>
        ) : null}
        {rpoItemsForStoreInward.styles.length ? (
          <>
            <Table
              bordered
              size="small"
              columns={stylesColumns}
              dataSource={rpoItemsForStoreInward.styles}
              rowKey={styleRowKey}
              pagination={false}
              scroll={scroll}
              title={() => 'Products Table'}
            />
            <br />
          </>
        ) : null}
        <Collapse>
          {storeInwardsOfRpo.map(storeinward => (
            <Panel
              header={`Store Inward #: ${
                storeinward.id
              } | Inward Date: ${moment(storeinward.inwardDate).format(
                'DD MMM YYYY'
              )}`}
              key={storeinward.id}
              extra={`${
                storeinward.updatedBy
                  ? `Updated By ${storeinward.updatedBy}`
                  : `Created By ${storeinward.createdBy}`
              } at ${moment(
                storeinward.updatedAt || storeinward.createdAt
              ).format('DD MMM YYYY')}`}
            >
              {storeinward.fabrics?.length ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={fabricsCollapseColumns}
                    dataSource={storeinward.fabrics || []}
                    rowKey={fabricRowKey}
                    pagination={false}
                    scroll={scroll}
                    title={() => 'Fabrics Table'}
                  />
                  <br />
                </>
              ) : null}
              {storeinward.accessories?.length ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={accessoryCollapseColumns}
                    dataSource={storeinward.accessories || []}
                    rowKey={accessoryRowKey}
                    pagination={false}
                    scroll={scroll}
                    title={() => 'Accessories Table'}
                  />
                  <br />
                </>
              ) : null}
              {storeinward.items?.length ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={itemsCollapseColumns}
                    dataSource={storeinward.items || []}
                    rowKey={itemRowKey}
                    pagination={false}
                    scroll={scroll}
                    title={() => 'Items Table'}
                  />
                  <br />
                </>
              ) : null}
              {storeinward.styles?.length ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={stylesCollapseColumns}
                    dataSource={storeinward.styles || []}
                    rowKey={styleRowKey}
                    pagination={false}
                    scroll={scroll}
                    title={() => 'Products Table'}
                  />
                  <br />
                </>
              ) : null}
              <Button
                className={classes.marginBetweenSelect}
                onClick={() =>
                  history.push(`/storeinward/update/${storeinward.id}`)
                }
                icon={<EditOutlined />}
              >
                Update
              </Button>
              <Button
                onClick={() =>
                  history.push(`/qualitycheck/create/${storeinward.id}`)
                }
                icon={<EditOutlined />}
              >
                Quality Check
              </Button>
            </Panel>
          ))}
        </Collapse>
      </div>
    </>
  );
};

ViewRPOInwards.propTypes = {
  match: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(ViewRPOInwards);
